import React from 'react';
import axios from 'axios';

const STATES = [
  {
    key: 'aperto',
    label: 'Aperto',
    icon: '✅',
    desc: 'Biglietti disponibili per tutti',
    gradient: 'linear-gradient(135deg, #059669, #10b981)',
    shadowColor: 'rgba(5, 150, 105, 0.35)',
  },
  {
    key: 'ultimiPostiUomo',
    label: 'Ultimi UOMO',
    icon: '👨',
    desc: 'Solo uomini possono acquistare',
    gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    shadowColor: 'rgba(37, 99, 235, 0.4)',
  },
  {
    key: 'ultimiPostiDonna',
    label: 'Ultimi DONNA',
    icon: '👩',
    desc: 'Solo donne possono acquistare',
    gradient: 'linear-gradient(135deg, #be123c, #e11d48)',
    shadowColor: 'rgba(190, 18, 60, 0.4)',
  },
  {
    key: 'soldOut',
    label: 'SOLD OUT',
    icon: '🔴',
    desc: 'Nessun biglietto acquistabile',
    gradient: 'linear-gradient(135deg, #6b7280, #9ca3af)',
    shadowColor: 'rgba(107, 114, 128, 0.35)',
  },
  {
    key: 'annullato',
    label: 'ANNULLATO',
    icon: '⛔',
    desc: 'Evento cancellato, niente biglietti',
    gradient: 'linear-gradient(135deg, #1e1e2e, #374151)',
    shadowColor: 'rgba(30, 30, 46, 0.5)',
  },
];

function getBg(state) {
  if (state === 'aperto') return '#f0fdf4';
  if (state === 'annullato') return '#f3f4f6';
  if (state === 'ultimiPostiUomo') return '#eff6ff';
  if (state === 'ultimiPostiDonna') return '#fdf2f8';
  return '#f9fafb';
}

function getBorder(state) {
  if (state === 'aperto') return '2px solid rgba(5, 150, 105, 0.3)';
  if (state === 'annullato') return '2px solid rgba(30, 30, 46, 0.3)';
  if (state === 'ultimiPostiUomo') return '2px solid rgba(37, 99, 235, 0.3)';
  if (state === 'ultimiPostiDonna') return '2px solid rgba(190, 18, 60, 0.3)';
  return '2px solid rgba(107, 114, 128, 0.3)';
}

function getColor(state) {
  if (state === 'aperto') return '#059669';
  if (state === 'annullato') return '#374151';
  if (state === 'ultimiPostiUomo') return '#2563eb';
  if (state === 'ultimiPostiDonna') return '#be123c';
  return '#6b7280';
}

export default function EventStatusSelector({ event, onRefresh, onChange, disabled, compact }) {
  const currentState = event.status >= 6
    ? 'annullato'
    : event.soldOut
    ? 'soldOut'
    : event.ultimiPostiDonna
    ? 'ultimiPostiDonna'
    : event.ultimiPostiUomo
    ? 'ultimiPostiUomo'
    : 'aperto';

  const handleSelect = async (key) => {
    if (key === currentState) return;

    if (event.status > 3 && key === 'soldOut') {
      window.Swal.fire(
        'Evento bloccato',
        'Non è possibile impostare SOLD OUT per un evento già iniziato.',
        'warning'
      );
      return;
    }

    let payload;
    if (key === 'aperto') {
      payload = { status: 0, soldOut: false, ultimiPostiUomo: false, ultimiPostiDonna: false };
    } else if (key === 'soldOut') {
      payload = { soldOut: true, ultimiPostiUomo: false, ultimiPostiDonna: false };
    } else if (key === 'annullato') {
      payload = { status: 6, soldOut: true, ultimiPostiUomo: false, ultimiPostiDonna: false };
    } else if (key === 'ultimiPostiUomo') {
      payload = { soldOut: false, ultimiPostiUomo: true, ultimiPostiDonna: false };
    } else if (key === 'ultimiPostiDonna') {
      payload = { soldOut: false, ultimiPostiUomo: false, ultimiPostiDonna: true };
    }

    // Form mode: delegate to parent
    if (onChange) {
      onChange(payload);
      return;
    }

    try {
      await axios.put(`/speeddate/api/events/${event.id}`, { id: event.id, ...payload });

      const labelMap = {
        aperto: 'Aperto a tutti',
        soldOut: 'SOLD OUT',
        annullato: 'ANNULLATO',
        ultimiPostiUomo: 'Ultimi posti UOMO',
        ultimiPostiDonna: 'Ultimi posti DONNA',
      };

      window.Swal.fire({
        title: key === 'aperto' ? '✅ Biglietti disponibili' : `🚫 ${labelMap[key]} Attivato!`,
        text: key === 'aperto'
          ? 'Le iscrizioni sono nuovamente aperte a tutti.'
          : `La restrizione "${labelMap[key]}" è attiva.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });

      if (onRefresh) onRefresh();
    } catch (err) {
      window.Swal.fire('Errore', "Impossibile aggiornare lo stato dell'evento", 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '4px' : '8px' }}>
      <select
        value={currentState}
        onChange={(e) => handleSelect(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: compact ? '5px 8px' : '12px 16px',
          borderRadius: compact ? '6px' : '12px',
          border: getBorder(currentState),
          background: getBg(currentState),
          color: getColor(currentState),
          fontSize: compact ? '0.75rem' : '0.95rem',
          fontWeight: compact ? 600 : 700,
          fontFamily: 'inherit',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'auto',
          WebkitAppearance: 'auto',
        }}
      >
        {STATES.map((s) => {
          const isDisabled = event.status > 3 && s.key === 'soldOut';
          const optStyle = { fontWeight: currentState === s.key ? 700 : 400 };
          if (compact) {
            return (
              <option key={s.key} value={s.key} disabled={isDisabled} style={optStyle}>
                {s.icon} {s.label}
              </option>
            );
          }
          return (
            <option key={s.key} value={s.key} disabled={isDisabled} style={optStyle}>
              {s.icon} {s.label} — {s.desc}
            </option>
          );
        })}
      </select>

    </div>
  );
}